import { BarChart3, ChevronRight, FileText, ShoppingBag, TrendingUp, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReportAdmin, getReportAdminRevenue } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportDashboard.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

export default function ReportDashboard() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canViewDashboard = hasPermission(PERMISSIONS.REPORT.DASHBOARD_VIEW);
    const canViewCustomer = hasPermission(PERMISSIONS.REPORT.CUSTOMER_VIEW);
    const canViewRevenue = hasPermission(PERMISSIONS.REPORT.REVENUE_VIEW);
    const canViewEndDay = hasPermission(PERMISSIONS.REPORT.END_DAY_VIEW);
    const canViewSales = hasPermission(PERMISSIONS.REPORT.SALES_VIEW);
    const canViewSalesDetail = hasPermission(PERMISSIONS.REPORT.SALES_DETAIL_VIEW);

    const [loading, setLoading] = useState(true);
    const [todayCustomers, setTodayCustomers] = useState(0);
    const [changePercent, setChangePercent] = useState(0);
    const [todayRevenue, setTodayRevenue] = useState(0);
    const [changePercentRevenue, setChangePercentRevenue] = useState(0);

    const fetchData = useCallback(async () => {
        if (!canViewDashboard) { setLoading(false); return; }
        setLoading(true);
        try {
            const [custRes, revRes] = await Promise.all([
                getReportAdmin(),
                getReportAdminRevenue(),
            ]);
            setTodayCustomers(custRes.data?.todayCustomers || 0);
            setChangePercent(custRes.data?.changePercent || 0);
            setTodayRevenue(revRes.data?.todayRevenue || 0);
            setChangePercentRevenue(revRes.data?.changePercent || 0);
        } catch (err) {
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    }, [canViewDashboard]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const reportLinks = [
        {
            to: '/admin/reports/customers',
            icon: <Users />,
            iconClass: styles.reportIconPurple,
            title: 'Báo cáo khách hàng',
            desc: 'Lượng khách theo ngày, giờ, thứ',
            show: canViewCustomer,
        },
        {
            to: '/admin/reports/revenue',
            icon: <BarChart3 />,
            iconClass: styles.reportIconTeal,
            title: 'Báo cáo doanh thu',
            desc: 'Phân tích doanh thu theo thời gian',
            show: canViewRevenue,
        },
        {
            to: '/admin/reports/end-day',
            icon: <FileText />,
            iconClass: styles.reportIconOrange,
            title: 'Báo cáo cuối ngày',
            desc: 'Tổng kết thu chi trong ngày',
            show: canViewEndDay,
        },
        {
            to: '/admin/reports/products',
            icon: <TrendingUp />,
            iconClass: styles.reportIconRed,
            title: 'Báo cáo bán hàng',
            desc: 'Lợi nhuận theo hoá đơn',
            show: canViewSales,
        },
        {
            to: '/admin/reports/products-detail',
            icon: <ShoppingBag />,
            iconClass: styles.reportIconTeal,
            title: 'Chi tiết bán hàng',
            desc: 'Chi tiết lợi nhuận theo giao dịch',
            show: canViewSalesDetail,
        },
    ];

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <h1 className={styles.title}>Báo cáo cửa hàng</h1>
                <p className={styles.subtitle}>Tổng quan hoạt động hôm nay</p>
            </div>

            {/* Stat cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard} onClick={() => canViewCustomer && navigate('/admin/reports/customers')}>
                    <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Khách hàng</span>
                        <span className={styles.statSub}>Lượng khách hôm nay</span>
                        {loading ? (
                            <div className={styles.loading}><div className={styles.spinner} /></div>
                        ) : (
                            <>
                                <span className={styles.statValue}>{todayCustomers}</span>
                                {changePercent > 0 && (
                                    <span className={`${styles.statChange} ${styles.statChangeUp}`}>
                                        ▲ {changePercent.toFixed(1)}%
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${styles.statIcon} ${styles.statIconBlue}`}><Users /></div>
                </div>

                <div className={styles.statCard} onClick={() => canViewRevenue && navigate('/admin/reports/revenue')}>
                    <div className={styles.statInfo}>
                        <span className={styles.statLabel}>Doanh thu</span>
                        <span className={styles.statSub}>Doanh thu thuần hôm nay</span>
                        {loading ? (
                            <div className={styles.loading}><div className={styles.spinner} /></div>
                        ) : (
                            <>
                                <span className={styles.statValue}>{formatCurrency(todayRevenue)}</span>
                                {changePercentRevenue > 0 && (
                                    <span className={`${styles.statChange} ${styles.statChangeUp}`}>
                                        ▲ {changePercentRevenue.toFixed(1)}%
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${styles.statIcon} ${styles.statIconGreen}`}><TrendingUp /></div>
                </div>
            </div>

            {/* Report links */}
            <div className={styles.cardsGrid}>
                {reportLinks.filter((r) => r.show).map((r) => (
                    <div key={r.to} className={styles.reportCard} onClick={() => navigate(r.to)}>
                        <div className={`${styles.reportIcon} ${r.iconClass}`}>{r.icon}</div>
                        <div className={styles.reportInfo}>
                            <div className={styles.reportTitle}>{r.title}</div>
                            <div className={styles.reportDesc}>{r.desc}</div>
                        </div>
                        <ChevronRight size={18} className={styles.reportArrow} />
                    </div>
                ))}
            </div>
        </div>
    );
}
